import type { BarrierConfig, LevelLayer, LevelPiece, LevelPieceType, TableConfig, TableShape, TableTopScale } from "./level_config.js";

export const MAX_TABLES = 4;

interface RawPiece
{
  id?: unknown;
  type?: unknown;
  x?: unknown;
  y?: unknown;
  width?: unknown;
  height?: unknown;
  variant?: unknown;
  offsetX?: unknown;
  offsetY?: unknown;
  offsetZ?: unknown;
  lying?: unknown;
  rotationDegX?: unknown;
  rotationDegY?: unknown;
  rotationDegZ?: unknown;
}

export function parsePayloadLayers( raw_layers_value: unknown ): LevelLayer[]
{
  const raw_layers = Array.isArray( raw_layers_value ) ? raw_layers_value : [];

  return raw_layers.map( ( raw_layer: any ) =>
  {
    const grid: number[][] = Array.isArray( raw_layer?.grid )
      ? raw_layer.grid.map( ( row: unknown ) =>
          Array.isArray( row ) ? row.map( ( cell: unknown ) => Number( cell ) || 0 ) : []
        )
      : [];

    const raw_pieces: RawPiece[] = Array.isArray( raw_layer?.pieces ) ? raw_layer.pieces : [];

    const pieces: LevelPiece[] = raw_pieces.map( ( piece, index ) => ( {
      id: Number( piece.id ) || index + 1,
      type: Number( piece.type ) as LevelPieceType,
      x: Number( piece.x ) || 0,
      y: Number( piece.y ) || 0,
      width: Number( piece.width ) || 1,
      height: Number( piece.height ) || 1,
      variant: piece.variant === undefined ? undefined : Number( piece.variant ),
      offsetX: piece.offsetX === undefined ? undefined : Number( piece.offsetX ),
      offsetY: piece.offsetY === undefined ? undefined : Number( piece.offsetY ),
      offsetZ: piece.offsetZ === undefined ? undefined : Number( piece.offsetZ ),
      lying: piece.lying === true,
      rotationDegX: piece.rotationDegX === undefined ? undefined : Number( piece.rotationDegX ),
      rotationDegY: piece.rotationDegY === undefined ? undefined : Number( piece.rotationDegY ),
      rotationDegZ: piece.rotationDegZ === undefined ? undefined : Number( piece.rotationDegZ ),
    } ) );

    return { grid, pieces };
  } );
}

/**
 * The optional authored tabletop tilt (see LevelConfig.tiltDeg). Read straight
 * off whichever object carries it — a table entry for a multi-table level, the
 * level payload itself for a single-table one — so both shapes of JSON spell
 * the slope the same way. Signed: a negative angle tips the other way, and 0
 * is dropped so a flat table never carries the field at all.
 */
export function parsePayloadTilt( raw: unknown ): { tiltDeg?: number }
{
  const value = ( raw as { tiltDeg?: unknown } | null | undefined )?.tiltDeg;
  if ( value === undefined || value === null )
    return {};

  const parsed = Number( value );

  return Number.isFinite( parsed ) && parsed !== 0 ? { tiltDeg: parsed } : {};
}

/**
 * Which earlier table this one is stood on top of — see TableConfig.liftFrom.
 * Only a whole, non-negative index survives; the game ignores one that does
 * not point backwards, so the parser does not need to know the final order.
 */
function parsePayloadLiftFrom( raw: unknown ): { liftFrom?: number }
{
  const value = ( raw as { liftFrom?: unknown } | null | undefined )?.liftFrom;
  if ( value === undefined || value === null )
    return {};

  const parsed = Number( value );

  return Number.isInteger( parsed ) && parsed >= 0 ? { liftFrom: parsed } : {};
}

/** Which earlier table this one stands on — see TableConfig.standOn. */
function parsePayloadStandOn( raw: unknown ): { standOn?: number }
{
  const value = ( raw as { standOn?: unknown } | null | undefined )?.standOn;
  if ( value === undefined || value === null )
    return {};

  const parsed = Number( value );

  return Number.isInteger( parsed ) && parsed >= 0 ? { standOn: parsed } : {};
}

const TABLE_SHAPES: readonly string[] = [ "rectangular", "round", "oval" ];

export function parsePayloadShape( raw: unknown ): { shape?: TableShape }
{
  const value = ( raw as { shape?: unknown } | null | undefined )?.shape;

  return typeof value === "string" && TABLE_SHAPES.includes( value ) && value !== "rectangular"
    ? { shape: value as TableShape }
    : {};
}

export function parsePayloadHang( raw: unknown ): { hang?: true }
{
  return ( raw as { hang?: unknown } | null | undefined )?.hang === true ? { hang: true } : {};
}

export function parsePayloadElevator( raw: unknown ): { elevator?: true }
{
  return ( raw as { elevator?: unknown } | null | undefined )?.elevator === true ? { elevator: true } : {};
}

// The sliding table is client-only for now: a level JSON — from the server or a
// test_level URL — must not be able to switch it on. Only levels built in client
// code (SlideTableShowcase) carry `slide`. Uncomment this and its call in
// parsePayloadTables() once the level data is meant to drive it.
//
// export function parsePayloadSlide( raw: unknown ): { slide?: true }
// {
//   return ( raw as { slide?: unknown } | null | undefined )?.slide === true ? { slide: true } : {};
// }

// A tabletop may be shrunk to a tenth of what the content asks for, or grown to
// four times it. Past that it is a typo rather than a level, and a table wide
// enough to swallow the field would only fail to place later, further from the
// cause.
const MIN_TOP_SCALE = 0.1;
const MAX_TOP_SCALE = 4;

function parseTopScaleAxis( value: unknown, axis: string, warn_tag?: string ): number | undefined
{
  if ( value === undefined || value === null )
    return undefined;

  const parsed = Number( value );

  if ( !Number.isFinite( parsed ) || parsed < MIN_TOP_SCALE || parsed > MAX_TOP_SCALE )
  {
    console.warn(
      `[LevelPayload]${warn_tag ? " " + warn_tag : ""}: topScale.${axis} ${String( value )} is not a `
      + `number in ${MIN_TOP_SCALE}..${MAX_TOP_SCALE} — ignored`
    );
    return undefined;
  }

  // 1 is what the table would do anyway; dropping it keeps a parsed config free
  // of fields that say nothing, the same way parsePayloadTilt drops a flat 0.
  return parsed === 1 ? undefined : parsed;
}

/**
 * The optional tabletop size multiplier — see TableConfig.topScale. Read
 * straight off whichever object carries it, so a table entry in a multi-table
 * level and the payload itself in a single-table one both spell it the same.
 */
export function parsePayloadTopScale( raw: unknown, warn_tag?: string ): { topScale?: TableTopScale }
{
  const value = ( raw as { topScale?: unknown } | null | undefined )?.topScale;

  if ( !value || typeof value !== "object" )
    return {};

  const width = parseTopScaleAxis( ( value as { width?: unknown } ).width, "width", warn_tag );
  const depth = parseTopScaleAxis( ( value as { depth?: unknown } ).depth, "depth", warn_tag );

  return width === undefined && depth === undefined
    ? {}
    : { topScale: { ...( width === undefined ? {} : { width } ), ...( depth === undefined ? {} : { depth } ) } };
}

const BARRIER_HEADS: readonly number[] = [ 0, 1 ];
// 1 = sky, 2 = left side, 3 = right side. 0 (a ground post) is retired.
const BARRIER_MOUNTS: readonly number[] = [ 1, 2, 3 ];

function optionalFinite( value: unknown ): number | undefined
{
  if ( value === undefined || value === null )
    return undefined;

  const parsed = Number( value );

  return Number.isFinite( parsed ) ? parsed : undefined;
}

export function parsePayloadBarriers( raw: unknown, warn_tag?: string ): { barriers?: BarrierConfig[] }
{
  const value = ( raw as { barriers?: unknown } | null | undefined )?.barriers;

  if ( !Array.isArray( value ) )
    return {};

  const barriers: BarrierConfig[] = [];

  value.forEach( ( entry: any, index ) =>
  {
    const head = Number( entry?.head ?? 0 );
    const mount = Number( entry?.mount ?? 1 );
    const x = optionalFinite( entry?.x );
    const y = optionalFinite( entry?.y );

    if ( !BARRIER_HEADS.includes( head ) || !BARRIER_MOUNTS.includes( mount ) || x === undefined || y === undefined )
    {
      console.warn(
        `[LevelPayload]${warn_tag ? " " + warn_tag : ""}: barriers[${index}] ${JSON.stringify( entry )} `
        + `needs head 0|1, mount 1|2|3 and numeric x, y — ignored`
      );
      return;
    }

    const z = optionalFinite( entry?.z );
    const angle = optionalFinite( entry?.angle );

    barriers.push( {
      head,
      mount,
      x,
      y,
      ...( z === undefined ? {} : { z } ),
      ...( angle === undefined || angle === 0 ? {} : { angle } ),
    } );
  } );

  return barriers.length > 0 ? { barriers } : {};
}

export function parsePayloadTables( raw_tables_value: unknown, warn_tag?: string ): TableConfig[]
{
  if ( !Array.isArray( raw_tables_value ) )
    return [];

  const tables = raw_tables_value
    .slice( 0, MAX_TABLES )
    .map( ( raw_table: any ): TableConfig =>
    {
      const rotation = raw_table?.rotation;
      const slot = raw_table?.slot;
      const has_slot = slot && Number.isFinite( Number( slot.u ) ) && Number.isFinite( Number( slot.v ) );

      return {
        layers: parsePayloadLayers( raw_table?.layers ),
        ...parsePayloadTilt( raw_table ),
        ...parsePayloadShape( raw_table ),
        ...parsePayloadHang( raw_table ),
        ...parsePayloadElevator( raw_table ),
        // ...parsePayloadSlide( raw_table ),  — client-only for now, see parsePayloadSlide above
        ...parsePayloadTopScale( raw_table, warn_tag ),
        ...parsePayloadLiftFrom( raw_table ),
        ...parsePayloadStandOn( raw_table ),
        ...parsePayloadBarriers( raw_table, warn_tag ),
        ...( rotation && typeof rotation === "object"
          ? {
              rotation: {
                angle: rotation.angle === undefined ? undefined : Number( rotation.angle ),
                spin: rotation.spin === undefined ? undefined : Number( rotation.spin ),
              }
            }
          : {} ),
        ...( has_slot ? { slot: { u: Number( slot.u ), v: Number( slot.v ) } } : {} ),
      };
    } )
    .filter( table => table.layers.length > 0 );

  if ( warn_tag && tables.length !== 0 && tables.length !== raw_tables_value.length )
    console.warn( `[${warn_tag}] ${raw_tables_value.length} tables in payload, ${tables.length} usable` );

  return tables;
}
